import { Head, Link, router } from "@inertiajs/react"
import { useRef, useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import {
  editEquipmentLogPath,
  equipmentLogPath,
  equipmentLogsPath,
  removeReceiptEquipmentLogPath,
  replaceReceiptEquipmentLogPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface EquipmentLogDetail {
  id: number
  equipment_id: number
  equipment_name: string | null
  vendor_name: string | null
  vendor_id: number | null
  employee_name: string | null
  log_date: string
  end_date: string | null
  amount: number
  billable_amount: number | null
  paid_to: string | null
  paid_by: string | null
  notes: string | null
  approval_notes: string | null
  start_odometer: number | null
  end_odometer: number | null
  odometer_usage: number | null
  status: number
  is_expense: boolean
  approved: boolean
  submitted: boolean
  approved_at: string | null
  approved_by_name: string | null
  closed_at: string | null
  created_at: string | null
  created_by_name: string | null
  has_receipt: boolean
  receipt_url: string | null
  receipt_filename: string | null
  receipt_content_type: string | null
}

interface Props {
  log: EquipmentLogDetail
  can_edit: boolean
  can_delete: boolean
  can_manage_receipt: boolean
  is_admin: boolean
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Equipment", href: "/equipment" },
  { title: "Maintenance Logs", href: "/equipment_logs" },
  { title: "Details", href: "#" },
]

function formatDate(dateStr: string | null): string {
  return (
    formatDateUtil(dateStr, {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    }) || "-"
  )
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

export default function Show({
  log,
  can_edit,
  can_delete,
  can_manage_receipt,
}: Props) {
  const [showUpload, setShowUpload] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDelete = () => {
    const label = log.is_expense ? "expense" : "log"
    if (!confirm(`Are you sure you want to delete this ${label}?`)) return
    router.delete(equipmentLogPath(log.id), { preserveState: false })
  }

  const handleDeleteReceipt = () => {
    if (!confirm("Are you sure you want to delete this receipt?")) return
    router.delete(removeReceiptEquipmentLogPath(log.id), {
      preserveState: false,
    })
  }

  const handleReplaceReceipt = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    router.patch(
      replaceReceiptEquipmentLogPath(log.id),
      { equipment_log: { receipt: file } },
      { forceFormData: true, preserveState: false },
    )
  }

  const title = log.is_expense ? "View Expense" : "View Log"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={title} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">{title}</h1>
          <div className="flex gap-2">
            {can_edit && (
              <Link
                href={editEquipmentLogPath(log.id)}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
              >
                Edit
              </Link>
            )}
            {can_delete && (
              <button
                onClick={handleDelete}
                className="bg-hpi-red rounded px-4 py-2 text-white hover:bg-red-700"
              >
                Delete
              </button>
            )}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Basic Information */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Details</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Date</dt>
                <dd>{formatDate(log.log_date)}</dd>
              </div>
              {log.end_date && (
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">End Date</dt>
                  <dd>{formatDate(log.end_date)}</dd>
                </div>
              )}
              {log.vendor_id && log.vendor_id > 0 && (
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">
                    {log.is_expense ? "Paid to" : "Vendor"}
                  </dt>
                  <dd>{log.vendor_name}</dd>
                </div>
              )}
              {!log.vendor_id && log.paid_to && (
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Paid to</dt>
                  <dd>{log.paid_to}</dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">
                  {log.is_expense ? "Employee" : "By"}
                </dt>
                <dd>{log.employee_name ?? "-"}</dd>
              </div>
              {log.is_expense && (
                <>
                  <div className="flex justify-between">
                    <dt className="text-hpi-med-gray">Amount</dt>
                    <dd className="font-semibold">
                      {formatCurrency(log.amount)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-hpi-med-gray">Payment</dt>
                    <dd>{log.paid_by ?? "-"}</dd>
                  </div>
                  {log.approved && log.approved_by_name && (
                    <div className="flex justify-between">
                      <dt className="text-hpi-med-gray">Approved by</dt>
                      <dd>{log.approved_by_name}</dd>
                    </div>
                  )}
                </>
              )}
              {log.start_odometer != null && log.start_odometer > 0 && (
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Odometer/Hours</dt>
                  <dd>
                    {log.start_odometer}
                    {log.end_odometer != null && log.end_odometer > 0 && (
                      <span> - {log.end_odometer}</span>
                    )}
                    {log.odometer_usage != null && log.odometer_usage > 0 && (
                      <span className="text-hpi-med-gray ml-1">
                        ({log.odometer_usage} usage)
                      </span>
                    )}
                  </dd>
                </div>
              )}
              {log.equipment_name && (
                <div className="flex justify-between">
                  <dt className="text-hpi-med-gray">Equipment</dt>
                  <dd>{log.equipment_name}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Notes */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">
              {log.is_expense ? "Explain Expense" : "Notes"}
            </h2>
            <p className="text-hpi-dark-gray whitespace-pre-wrap">
              {log.notes ?? "-"}
            </p>
            {log.is_expense && log.approval_notes && (
              <div className="text-hpi-red mt-4">
                Review Notes: {log.approval_notes}
              </div>
            )}
          </div>

          {/* Receipt (expenses only) */}
          {log.is_expense && (
            <div className="rounded-lg bg-white p-6 shadow">
              <h2 className="mb-4 text-lg font-semibold">Receipt</h2>
              {log.receipt_url ? (
                <div className="space-y-3">
                  <a
                    href={log.receipt_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-hpi-active-blue inline-flex items-center gap-1 hover:underline"
                  >
                    {log.receipt_content_type === "application/pdf" ? (
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    )}
                    {log.receipt_filename}
                  </a>
                  {can_manage_receipt && (
                    <div className="flex gap-2">
                      <button
                        onClick={handleDeleteReceipt}
                        className="text-hpi-red text-sm hover:underline"
                      >
                        Delete Receipt
                      </button>
                      <button
                        onClick={() => setShowUpload(true)}
                        className="text-hpi-active-blue text-sm hover:underline"
                      >
                        Replace Receipt
                      </button>
                    </div>
                  )}
                  {showUpload && (
                    <div className="border-hpi-light-gray mt-2 rounded border p-3">
                      <label className="text-hpi-dark-gray block text-sm font-medium">
                        Upload replacement receipt
                      </label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleReplaceReceipt}
                        className="mt-1 block text-sm"
                      />
                      <button
                        onClick={() => setShowUpload(false)}
                        className="text-hpi-med-gray mt-1 text-xs hover:underline"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <span className="text-hpi-med-gray">No receipt attached</span>
                  {can_manage_receipt && (
                    <div>
                      <label className="text-hpi-dark-gray block text-sm font-medium">
                        Upload receipt
                      </label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleReplaceReceipt}
                        className="mt-1 block text-sm"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Audit Trail (expenses only) */}
          {log.is_expense && log.submitted && (
            <div className="rounded-lg bg-white p-6 shadow md:col-span-2">
              <h2 className="mb-4 text-lg font-semibold">Audit Trail</h2>
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <h3 className="font-medium">Added</h3>
                  <div className="text-hpi-med-gray mt-1 text-sm">
                    {formatDate(log.created_at)}
                  </div>
                </div>
                <div>
                  <h3 className="font-medium">Submitted</h3>
                  <div className="text-hpi-med-gray mt-1 text-sm">
                    {formatDate(log.closed_at)}
                  </div>
                </div>
                <div>
                  <h3 className="font-medium">Approval</h3>
                  {log.approved ? (
                    <div className="mt-1 text-sm">
                      <div className="text-hpi-dark-green">Approved</div>
                      {log.approved_by_name && (
                        <div className="text-hpi-med-gray">
                          by {log.approved_by_name}
                        </div>
                      )}
                      <div className="text-hpi-med-gray">
                        {formatDate(log.approved_at)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-hpi-yellow mt-1 text-sm">
                      Pending Approval
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6">
          <Link
            href={equipmentLogsPath({
              equipment_id: log.equipment_id.toString(),
            })}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Log/Expense List
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
